import { Slot, Slottable } from '@radix-ui/react-slot';
import type { LucideIcon } from 'lucide-react';
import type { ComponentPropsWithoutRef, MouseEvent, Ref } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import { Spinner } from '../Spinner/Spinner';
import s from './Button.module.css';

export type ButtonVariant = 'primary' | 'fun' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = ComponentPropsWithoutRef<'button'> & {
  ref?: Ref<HTMLButtonElement>;
  /**
   * primary = berry (the main action) · fun = blue with dark text (Claim it, Paste a list) ·
   * secondary = white · ghost = no fill · danger = outlined crimson (never a filled red).
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconStart?: LucideIcon;
  iconEnd?: LucideIcon;
  fullWidth?: boolean;
  /** Shows a spinner, keeps the width, and ignores clicks while staying focusable. */
  loading?: boolean;
  /** Render the child element (e.g. a router Link) with button styling. */
  asChild?: boolean;
};

export function Button({
  variant = 'primary',
  size = 'md',
  iconStart,
  iconEnd,
  fullWidth,
  loading,
  asChild,
  className,
  children,
  onClick,
  type,
  ...rest
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  const iconSize = size === 'lg' ? 'lg' : 'md';

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <Comp
      className={cx(s.button, className)}
      data-variant={variant}
      data-size={size}
      data-full={fullWidth || undefined}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      {...(asChild ? {} : { type: type ?? 'button' })}
      {...rest}
    >
      {iconStart ? <Icon icon={iconStart} size={iconSize} className={s.content} /> : null}
      <Slottable>{children}</Slottable>
      {iconEnd ? <Icon icon={iconEnd} size={iconSize} className={s.content} /> : null}
      {loading ? <Spinner size="sm" label={null} className={s.spinner} /> : null}
    </Comp>
  );
}
